import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FooterContext from './FooterContext';
import isSSR from '../../../utils/isSSR';
import {
  COMPONENT_NAME_SITE_LOGO,
  DISPLAY_NAME_LANGUAGE_MENU,
  MARINE_SITE_NAME,
  PE_SITE_NAME,
  PSP_SITE_NAME,
} from '../../../utils/constants';
import { LANGUAGE_URL } from '../../Header/service/constants';
import { createObjectFromProps } from '../../../utils/object';
import { getSection } from '../utils';
import { useAppName, useLanguage, useLanguageSelectors, useRoute } from '../../../utils/sitecoreContext';
import { useConfiguration } from './utils';

const FooterProvider = ({ children, appNameConfig, fields }) => {
  const { t } = useTranslation();
  const config = useConfiguration(appNameConfig);
  const [isOpenMenu, setIsOpenMenu] = useState(false);
  const appName = useAppName();

  const navigationItems = getSection(fields, 'NavigationSection');
  const backToTopData = getSection(fields, 'BackToTopSection');
  const socialMediaSection = getSection(fields, 'SocialMediaSection');
  const legalSection = getSection(fields, 'LegalSection');
  const showGeoLocator = !!getSection(fields, 'GeoLocatorSection');

  const route = useRoute();
  const language = useLanguage();
  const languageSelectors = useLanguageSelectors();
  const languageMenu = useMemo(
    () =>
      isSSR()
        ? {}
        : {
            itemId: DISPLAY_NAME_LANGUAGE_MENU,
            itemUrl: LANGUAGE_URL,
            itemName: DISPLAY_NAME_LANGUAGE_MENU,
            itemDisplayName: DISPLAY_NAME_LANGUAGE_MENU,
            current: language,
            items: languageSelectors?.map(item => ({
              itemId: item.code,
              itemUrl: LANGUAGE_URL,
              itemName: item.code,
              itemDisplayName: item.code,
              label: t(`Shared.Footer.${[item.code]}Label`),
              url: { href: item.url },
            })),
            label: `${t('Shared.Header.language')}: ${language?.toUpperCase()}`,
          },
    [language, languageSelectors],
  );

  const siteLogo = useMemo(
    () =>
      createObjectFromProps(
        (route?.placeholders?.header || route?.placeholders?.['acura-header'])?.find(
          component => component?.componentName === COMPONENT_NAME_SITE_LOGO,
        ),
      ),
    [],
  );
  let geoLocationChangeButtonStyling = 'tertiaryLight';

  switch (appName) {
    case PE_SITE_NAME:
      geoLocationChangeButtonStyling = 'redUnderline';
      break;
    case MARINE_SITE_NAME:
    case PSP_SITE_NAME:
      geoLocationChangeButtonStyling = 'red';
      break;
    default:
      break;
  }

  const contextValue = useMemo(
    () => ({
      ...config,
      backToTopData,
      isOpenMenu,
      languageMenu,
      legalSection,
      navigationItems,
      setIsOpenMenu,
      showGeoLocator,
      siteLogo,
      socialMediaSection,
      geoLocationChangeButtonStyling,
    }),
    [
      isOpenMenu,
      language,
      navigationItems,
      backToTopData,
      socialMediaSection,
      legalSection,
      showGeoLocator,
      languageMenu,
      geoLocationChangeButtonStyling,
    ],
  );

  return <FooterContext.Provider value={contextValue}>{children}</FooterContext.Provider>;
};

FooterProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

export default FooterProvider;
